import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { Transaction } from '@atlaskit/editor-prosemirror/state';
import { DiffContent } from '@atlassian/bitkit-diff';

import { useParams } from 'src/router/hooks';
import authRequest, { jsonHeaders } from 'src/utils/fetch';

import { commitResponseFlagMessages as messages } from './code-suggestions.i18n';

export const formatRemovedLine = (
  content: string | null | undefined
): string => {
  if (!content) return '';
  if (content.charAt(0) === '+') return `- ${content.slice(1)}`;
  if (content.charAt(0) !== '-') return `- ${content}`;
  return content;
};

// remove the + or - from the diff line content
export const getDiffContentWithoutPrefix = ({
  content,
}: DiffContent): string => {
  if (!content) return '';
  if (content.charAt(0) === '+' || content.charAt(0) === '-')
    return content.slice(1);
  return content;
};

export const getDiffStartLineNum = (diffLine: DiffContent): number => {
  return diffLine.lineTo || diffLine.lineFrom || 0;
};

type SuggestionsBody = {
  message: string;
  suggestions: {
    comment_id: number;
    content: string;
    suggestion_index: number;
  }[];
};

const commitSuggestion = async (
  body: SuggestionsBody,
  repositoryOwner: string,
  repositorySlug: string,
  pullRequestId: string
): Promise<any> => {
  const url = `/!api/internal/repositories/${repositoryOwner}/${repositorySlug}/pullrequests/${pullRequestId}/suggestions/`;
  const response = await fetch(
    authRequest(url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: jsonHeaders,
    })
  );
  return response;
};

/**
 * A React hook that provides functionality to post code suggestions to a pull request.
 *
 * The hook returns an object with the following properties:
 * - `postCodeSuggestions`: A function that takes a `SuggestionsBody` object and makes a POST request to:
 *  -   1. Commit the suggestion to the pull request
 *  -   2. Update the comment object with the applied suggestion
 * - `loading`: A boolean indicating whether the code suggestion is being posted.
 * - `error`: Any error that occurred while posting the code suggestion.
 *
 */
export const usePostCodeSuggestions = (): {
  postCodeSuggestions: (
    body: SuggestionsBody
  ) => Promise<{ success: boolean; errorMessage?: string }>;
  loading: boolean;
} => {
  const {
    repositoryOwner: owner,
    repositorySlug: slug,
    pullRequestId: id,
  } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: string;
  }>();
  const [loading, setLoading] = useState<boolean>(false);
  const { formatMessage } = useIntl();

  const postCodeSuggestions = useCallback(
    async (body: SuggestionsBody) => {
      if (!owner || !slug || !id) {
        return {
          success: false,
          errorMessage: formatMessage(messages.errorInvalidParameters),
        };
      }
      setLoading(true);

      try {
        const res = await commitSuggestion(body, owner, slug, id);
        const resJson = await res.json();
        if (!res.ok) {
          throw new Error(resJson.error.message);
        }
        return { success: true, errorMessage: undefined };
      } catch (e) {
        return { success: false, errorMessage: e.message };
      } finally {
        setLoading(false);
      }
    },
    [owner, slug, id, formatMessage]
  );

  return {
    postCodeSuggestions,
    loading,
  };
};

export const insertCodeSuggestionNode = (tr: Transaction) => {
  const {
    selection: { from },
  } = tr;

  // Get the node at the current position
  const currentNode = tr.doc.nodeAt(from - 1);

  // create the extension node
  const extensionNode = tr.doc.type.schema.nodes.extension.createChecked({
    extensionType: 'com.atlassian.bbc.code-suggestions',
    extensionKey: 'codesuggestions:suggestion-node',
    parameters: {
      extensionTitle: 'Suggesting',
    },
  });

  // If the current node is an empty paragraph, insert the extension node before it
  if (
    currentNode &&
    currentNode.type.name === 'paragraph' &&
    currentNode.textContent === ''
  ) {
    tr.insert(from - 1, extensionNode);
  } else {
    // Insert the extension node at the current position
    tr.insert(from, extensionNode);
  }

  return tr;
};
